import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Shield, ArrowLeft } from "lucide-react";
import { useRazorpay } from "react-razorpay";

export default function BookingCheckout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { Razorpay } = useRazorpay();

  const propertyId = params.get("property_id");
  const checkIn = params.get("check_in");
  const checkOut = params.get("check_out");
  const guestsParam = Number(params.get("guests") || 2);

  const [property, setProperty] = useState(null);
  const [payConfig, setPayConfig] = useState({ enabled: false });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);

  useEffect(() => {
    if (!propertyId || !checkIn || !checkOut) {
      navigate("/search");
      return;
    }
    api.get(`/properties/${propertyId}`).then((r) => setProperty(r.data)).catch(() => navigate("/search"));
    api.get("/payments/config").then((r) => setPayConfig(r.data)).catch(() => {});
  }, [propertyId, checkIn, checkOut, navigate]);

  useEffect(() => {
    if (user && user !== false) {
      setName(user.name || "");
      setEmail(user.email || "");
      setPhone(user.phone || "");
    }
  }, [user]);

  if (!property) return <div className="text-center py-32 text-[hsl(100_3%_30%)]">Loading…</div>;

  const nights = Math.max(0, (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
  const subtotal = nights * property.price_per_night;
  const taxes = Math.round(subtotal * 0.12);
  const total = subtotal + taxes;

  const handlePay = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone) return toast.error("Please fill your details");
    setSubmitting(true);
    try {
      // 1. Create booking
      const { data: booking } = await api.post("/bookings", {
        property_id: propertyId,
        check_in: checkIn,
        check_out: checkOut,
        guests: guestsParam,
        guest_name: name,
        guest_email: email,
        guest_phone: phone,
        special_requests: notes,
      });

      // If payment gateway disabled — mark booking as pending "pay at property"
      if (!payConfig.enabled) {
        toast.success("Booking request received!");
        setSuccessBooking(booking);
        setSubmitting(false);
        return;
      }

      // 2. Create Razorpay order
      const { data: order } = await api.post("/payments/create-order", { booking_id: booking.id });

      // 3. Open Razorpay checkout
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "NewDayStays",
        description: `${property.title} · ${nights} nights`,
        order_id: order.order_id,
        prefill: { name, email, contact: phone },
        theme: { color: "#C86B53" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              booking_id: booking.id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            const confirmed = await api.get(`/bookings/${booking.id}`);
            setSuccessBooking(confirmed.data);
            toast.success("Payment successful! Booking confirmed.");
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast.info("Payment cancelled — your booking is on hold.");
          },
        },
      };
      new Razorpay(options).open();
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (successBooking) {
    const isPaid = successBooking.payment_status === "paid";
    return (
      <div className="mx-auto max-w-2xl px-6 md:px-10 py-20 text-center" data-testid="booking-success">
        <CheckCircle2 className="h-16 w-16 mx-auto text-[hsl(165_37%_17%)] mb-6" />
        <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)]">{isPaid ? "Booking confirmed" : "Booking request received"}</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl mt-3">See you soon at {property.title}</h1>
        <p className="mt-4 text-[hsl(100_3%_30%)]">
          Confirmation ID: <span className="font-mono">{successBooking.id.slice(0, 8).toUpperCase()}</span>
        </p>
        <div className="mt-8 text-left bg-white border border-[hsl(165_37%_17%/0.1)] rounded-md p-6 space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Check in</span><span>{checkIn}</span></div>
          <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Check out</span><span>{checkOut}</span></div>
          <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Guests</span><span>{guestsParam}</span></div>
          <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Total</span><span>₹{total.toLocaleString("en-IN")}</span></div>
          <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Status</span><span className="font-medium">{isPaid ? "Confirmed" : "Awaiting payment"}</span></div>
        </div>
        <Button onClick={() => navigate("/my-bookings")} className="mt-8 btn-forest rounded-full px-8" data-testid="view-bookings-btn">
          View my bookings
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 md:px-10 py-10" data-testid="checkout-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[hsl(100_3%_30%)] mb-6 hover:text-[hsl(10_51%_55%)]">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Left form */}
        <div className="lg:col-span-3">
          <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)]">Confirm & pay</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 mb-8">Your details</h1>

          <form onSubmit={handlePay} className="space-y-5 max-w-lg">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[hsl(100_3%_30%)]">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="checkout-name" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[hsl(100_3%_30%)]">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="checkout-email" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-[hsl(100_3%_30%)]">Phone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="checkout-phone" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="notes" className="text-xs uppercase tracking-widest text-[hsl(100_3%_30%)]">Special requests (optional)</Label>
              <Textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-notes" className="mt-2" />
            </div>

            <div className="pt-4 flex items-center gap-2 text-xs text-[hsl(100_3%_30%)]">
              <Shield className="h-4 w-4" /> {payConfig.enabled ? "Secure payment via Razorpay · UPI · Cards · Netbanking" : "Payment gateway not configured — you'll pay at the property"}
            </div>

            <Button type="submit" disabled={submitting} className="w-full btn-terracotta rounded-full h-12" data-testid="pay-btn">
              {submitting ? "Processing…" : payConfig.enabled ? `Pay ₹${total.toLocaleString("en-IN")}` : "Confirm booking"}
            </Button>
          </form>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-2">
          <div className="border border-[hsl(165_37%_17%/0.1)] rounded-md p-6 bg-white" data-testid="checkout-summary">
            <div className="flex gap-4 pb-5 border-b border-[hsl(165_37%_17%/0.08)]">
              <img src={property.images?.[0]} alt="" className="h-24 w-24 rounded object-cover" />
              <div className="min-w-0">
                <p className="text-xs text-[hsl(100_3%_30%)]">{property.property_type} · {property.location}</p>
                <h3 className="font-serif-display text-lg leading-tight mt-1 truncate">{property.title}</h3>
                <p className="text-xs text-[hsl(100_3%_30%)] mt-1">{property.address}</p>
              </div>
            </div>
            <div className="pt-5 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Check in</span><span>{checkIn}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Check out</span><span>{checkOut}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Guests</span><span>{guestsParam}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(100_3%_30%)]">Nights</span><span>{nights}</span></div>
              <div className="pt-3 border-t border-[hsl(165_37%_17%/0.08)] flex justify-between">
                <span>Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-[hsl(100_3%_30%)]">
                <span>Taxes (12%)</span><span>₹{taxes.toLocaleString("en-IN")}</span>
              </div>
              <div className="pt-3 border-t border-[hsl(165_37%_17%/0.08)] flex justify-between font-medium text-base">
                <span>Total</span><span>₹{total.toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
